import React, { useCallback, useEffect, useState } from "react";
import { Col, OverlayTrigger, Row, Tooltip } from "react-bootstrap";

import DstButton from "../../../../components/DstButton/DstButton";
import DstTable from "../../../../components/DstTable/DstTable";
import Popup from "../../../../components/Popup/Popup";
import { useSession } from "../../../../contexts/SessionContext";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import translate from "../../../../services/Translate";
import { Attendee } from "../../Meeting.d";
import { AttendanceProps, MeetingAttendanceStatus } from "./Attendance.d";
import {
  fetchAttendanceSheet,
  fetchMeetingAttendance,
  generateAttendanceLink,
  lockAttendance,
  syncEdusignAttendance,
  updateMultipleAttendance,
} from "./Attendance.function";

const Attendance = ({
  meetingId,
  edusignLocked,
  edusignCourseId,
  isMeetingFinished,
  isMeetingMandatory,
  refreshMeeting,
}: AttendanceProps) => {
  const { language } = useSession();
  const [attendance, setAttendance] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<MeetingAttendanceStatus | null>(null);

  const _refreshAttendanceData = useCallback(() => {
    fetchMeetingAttendance(
      meetingId,
      (data: any[]) => {
        setAttendance(data);
        setSelectedRows([]);
      },
      setLoading
    );
  }, [meetingId, setLoading]);

  useEffect(() => {
    if (meetingId && edusignLocked) {
      _refreshAttendanceData();
    }
  }, [meetingId, edusignLocked, language, _refreshAttendanceData]);

  const columns = [
    { key: "full_name", name: translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_HEADER.FULL_NAME") },
    { key: "email", name: translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_HEADER.USERNAME") },
    {
      key: "cohorts",
      name: translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_HEADER.COHORT"),
      render: (item: Attendee) =>
        item.related_cohorts.length > 0 ? item.related_cohorts.map((c: any) => c.name).join(", ") : "No Cohort",
    },
    {
      key: "has_signed",
      name: translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_HEADER.EDUSIGN_STATUS"),
      render: (item: Attendee) => {
        return item.has_signed ? (
          <span className="text-success">
            <span className="dst-icon-check me-2" />
            {translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.EDUSIGN_STATUS.SIGNED")}
          </span>
        ) : (
          <span className="orange-font">
            <span className="dst-icon-alert-square me-2" />
            {translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.EDUSIGN_STATUS.NOT_SIGNED")}
          </span>
        );
      },
    },
    {
      key: "attended",
      name: translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_HEADER.ATTENDED"),
      render: (item: Attendee) => {
        if (item.has_signed) return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.ATTENDED");
        switch (item.attended) {
          case "attended":
            return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.ATTENDED");
          case "forgotten_signature":
            return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.FORGOTTEN_SIGNATURE");
          case "justified_absence":
            return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.JUSTIFIED_ABSENCE");
          case "absent":
            return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.ABSENT");
          case null:
            return translate(language, "PAGES.MEETING.ATTENDANCE.COLUMN_BODY.ATTENDED.UNKNOWN_STATUS");
        }
      },
    },
  ];

  const _confirmAttendanceUpdate = (status: MeetingAttendanceStatus) => {
    if (selectedRows.length === 0) return;

    setPendingAction(status);
    setShowConfirmModal(true);
  };

  return (
    <div>
      <Row className="mb-3 d-flex justify-content-between">
        <Col className="d-flex">
          <OverlayTrigger
            trigger={["hover", "focus"]}
            placement="top"
            overlay={
              isMeetingMandatory ? (
                <React.Fragment></React.Fragment>
              ) : (
                <Tooltip id="tooltip-get-attendance-link-disabled">
                  {translate(language, "PAGES.MEETING.ATTENDANCE.TOOLTIPS.OPTIONAL_MEETING")}
                </Tooltip>
              )
            }
          >
            <span className="d-inline-block">
              <DstButton
                value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.GET_ATTENDANCE_LINK")}
                btnImageBefore={<span className="dst-icon-link-01 me-2" />}
                variant="light"
                btnClass="me-2 my-1"
                disabled={!isMeetingMandatory}
                clickFunction={() =>
                  edusignCourseId
                    ? setShowWarningModal(true)
                    : generateAttendanceLink(meetingId, language, setLoading, refreshMeeting)
                }
              />
            </span>
          </OverlayTrigger>
          {edusignLocked && (
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.ATTENDANCE_SHEET")}
              btnImageBefore={<span className="dst-icon-download-02 me-2" />}
              variant="secondary"
              btnClass="me-2 my-1"
              clickFunction={() => fetchAttendanceSheet(meetingId, language)}
              mock={loading}
              btnWidth="140px"
              btnHeight="auto"
            />
          )}
          {edusignLocked && (
            <PermissionComponent
              neededPermission={[{ methods: ["POST"], route: "/meetings/*/attendance/sync-edusign" }]}
            >
              <DstButton
                value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.SYNC_EDUSIGN")}
                btnClass="ms-1 my-1"
                btnImageBefore={<span className="dst-icon-refresh-ccw-02 me-2" />}
                clickFunction={() => syncEdusignAttendance(meetingId, language, setLoading, _refreshAttendanceData)}
                disabled={loading}
              />
            </PermissionComponent>
          )}
        </Col>
        {selectedRows.length > 0 && (
          <Col md className="text-md-end">
            <span className="xs-regular blue-font me-2">
              {selectedRows.length} {translate(language, "PAGES.MEETING.ATTENDANCE.SELECTED_ATTENDEES")}
            </span>
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.DELETE_FROM_ATTENDANCE")}
              variant="outline-danger border-0"
              btnClass="tinted-red-background me-2 my-1"
              btnImageBefore={<span className="dst-icon-trash-03 me-2" />}
              clickFunction={() => alert("WIP")}
            />
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.JUSTIFIED_ABSENCE")}
              variant="light"
              btnClass="me-2 my-1 border-0"
              btnImageBefore={<span className="dst-icon-file-check-02 me-2" />}
              clickFunction={() => _confirmAttendanceUpdate(MeetingAttendanceStatus.JUSTIFIED_ABSENCE)}
              disabled={loading || !isMeetingFinished}
            />
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.FORGOTTEN_SIGNATURE")}
              variant="success"
              btnClass="my-1 border-0 medium-green-background"
              btnImageBefore={<span className="dst-icon-clipboard-check me-2" />}
              clickFunction={() => _confirmAttendanceUpdate(MeetingAttendanceStatus.FORGOTTEN_SIGNATURE)}
              disabled={loading || !isMeetingFinished}
            />
          </Col>
        )}
      </Row>
      <DstTable
        columns={columns}
        data={attendance}
        loading={loading}
        itemsPerPage={20}
        pagination
        striped
        selectable
        selectionKey="user_id"
        selectedItems={selectedRows}
        onRowSelect={(selected: any[]) => setSelectedRows(selected)}
      />
      {!edusignLocked && (
        <div className="mt-3 text-center">
          <OverlayTrigger
            trigger={["hover", "focus"]}
            placement="top"
            overlay={
              isMeetingMandatory ? (
                <React.Fragment></React.Fragment>
              ) : (
                <Tooltip id="tooltip-lock-attendance-disabled">
                  {translate(language, "PAGES.MEETING.ATTENDANCE.TOOLTIPS.OPTIONAL_MEETING")}
                </Tooltip>
              )
            }
          >
            <span className="d-inline-block">
              <DstButton
                value={translate(language, "PAGES.MEETING.ATTENDANCE.BUTTONS.LOCK_ATTENDANCE")}
                variant="danger"
                disabled={!isMeetingMandatory}
                clickFunction={() =>
                  !edusignCourseId
                    ? alert(translate(language, "PAGES.MEETING.ERRORS.GENERATE_ATTENDANCE_FIRST"))
                    : lockAttendance(meetingId, language, setLoading, refreshMeeting)
                }
              />
            </span>
          </OverlayTrigger>
        </div>
      )}
      <Popup
        show={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        modalTitle={translate(language, "PAGES.MEETING.ATTENDANCE.MODAL_LINK_ALREADY_GENERATED.TITLE")}
      >
        <div
          dangerouslySetInnerHTML={{
            __html: translate(language, "PAGES.MEETING.ATTENDANCE.MODAL_LINK_ALREADY_GENERATED.EXPLAIN"),
          }}
        />
      </Popup>
      <Popup
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        modalTitle={translate(language, "PAGES.MEETING.ATTENDANCE.MODAL_APPROVAL_CHANGE_ATTENDANCE.TITLE")}
      >
        <div className="p-3">
          <div className="d-flex justify-content-center mt-2">
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.MODAL_APPROVAL_CHANGE_ATTENDANCE.CANCEL")}
              variant="light"
              btnClass="me-3"
              clickFunction={() => setShowConfirmModal(false)}
            />
            <DstButton
              value={translate(language, "PAGES.MEETING.ATTENDANCE.MODAL_APPROVAL_CHANGE_ATTENDANCE.CONFIRM")}
              variant="primary"
              clickFunction={() => {
                setShowConfirmModal(false);
                if (pendingAction) {
                  updateMultipleAttendance(
                    meetingId,
                    selectedRows.map((row) => row.user_id),
                    pendingAction,
                    language,
                    setLoading,
                    _refreshAttendanceData
                  );
                }
              }}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default Attendance;
